import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { AdminCapability, AppCapability, hasAdminCapability, hasAppCapability, useAuth } from '@services/auth';
import { useI18n } from '../i18n';
import { useMediaQuery } from '../hooks/useMediaQuery';
import Topbar from '../shared/Topbar';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import AdminViewAsStrip from '../components/AdminViewAsStrip';
import MiniCalendar from '../components/MiniCalendar';
import SidebarFAQ from '../components/SidebarFAQ';
import DeadlineChecker from '../components/DeadlineChecker';
import './admin-layout.css';
import '../styles/gantt.css';

const MOBILE_SIDEBAR_MQ = '(max-width: 991.98px)';

type NavItem = {
	label: string;
	to: string;
	icon: string;
	roles: Array<'admin' | 'company' | 'gardener' | 'seeker'>;
	tip?: string;
	adminCapability?: AdminCapability;
	appCapability?: AppCapability;
	/** Hide from sidebar (e.g. Monitoring until sensors are available); route and page stay for future use */
	hidden?: boolean;
};

type NavSection = {
	key: string;
	title?: string;
	items: NavItem[];
};

const NAV_SECTIONS: NavSection[] = [
	{
		key: 'overview',
		items: [
			{
				label: 'Dashboard',
				to: '/app/dashboard',
				icon: 'bi-speedometer2',
				roles: ['admin', 'company', 'gardener'],
				appCapability: 'dashboard'
			},
			{
				label: 'Marketplace',
				to: '/app/plans',
				icon: 'bi-shop',
				roles: ['admin', 'company', 'gardener'],
				appCapability: 'marketplace'
			},
			{
				label: 'Direct plans',
				to: '/app/direct-plans',
				icon: 'bi-person-lines-fill',
				roles: ['admin', 'company'],
				appCapability: 'direct_plans',
				tip: 'Plans exclusive to your company (drafts, invites, client plans)'
			},
			{
				label: 'Monitoring',
				to: '/app/analytics',
				icon: 'bi-graph-up',
				roles: ['admin', 'company'],
				hidden: true // Re-enable when sensors/IoT are available
			}
		]
	},
	{
		key: 'gardener',
		title: 'Gardener',
		items: [
			{
				label: 'Projects',
				to: '/app/projects',
				icon: 'bi-kanban',
				roles: ['admin', 'company', 'gardener'],
				appCapability: 'projects',
				tip: 'Projects and sent offers (use Sent offers tab)'
			},
			{
				label: 'Schedule',
				to: '/app/schedule',
				icon: 'bi-calendar3',
				roles: ['admin', 'company', 'gardener'],
				appCapability: 'schedule',
				tip: 'View tasks from project timelines'
			},
			{
				label: 'Tasks',
				to: '/app/tasks',
				icon: 'bi-check2-circle',
				roles: ['admin', 'company', 'gardener'],
				appCapability: 'tasks'
			},
			{
				label: 'Team',
				to: '/app/team',
				icon: 'bi-people',
				roles: ['admin', 'company'],
				appCapability: 'team',
				tip: 'View team attendance and check-ins'
			},
			{
				label: 'Plant Library',
				to: '/app/plants',
				icon: 'bi-flower3',
				roles: ['admin', 'gardener'],
				hidden: true // Mock data only — re-enable when real plant library is ready
			}
		]
	},
	{
		key: 'seeker',
		title: 'Garden Seekers',
		items: [
			{
				label: 'My Plans',
				to: '/app/seeker/plans',
				icon: 'bi-journal-text',
				roles: ['admin', 'seeker'],
				appCapability: 'my_plans'
			},
			{
				label: 'My Projects',
				to: '/app/seeker/projects',
				icon: 'bi-kanban',
				roles: ['admin', 'seeker'],
				appCapability: 'my_projects',
				tip: 'Accepted offers become projects — track progress here'
			},
			{
				label: 'Create Plan',
				to: '/app/seeker/create-plan?mode=company-direct',
				icon: 'bi-plus-circle',
				roles: ['admin', 'seeker', 'company'],
				appCapability: 'create_plan'
			}
		]
	},
		{
			key: 'admin',
			title: 'Admin',
			items: [
			{
				label: 'Admin Insights',
				to: '/app/admin/analytics',
				icon: 'bi-bar-chart-line',
				roles: ['admin'],
				adminCapability: 'admin_analytics'
			},
			{
				label: 'Engagement',
				to: '/app/admin/engagement',
				icon: 'bi-people-fill',
				roles: ['admin'],
				adminCapability: 'admin_engagement',
				tip: 'Traffic, registrations, and visitor mix over time'
			},
			{
				label: 'User activity',
				to: '/app/admin/user-activity',
				icon: 'bi-person-lines-fill',
				roles: ['admin'],
				adminCapability: 'admin_user_activity',
				tip: 'Time in app, plans, and events per user'
			},
			{
				label: 'Moderation',
				to: '/app/admin/moderation',
				icon: 'bi-shield-check',
				roles: ['admin'],
				adminCapability: 'admin_moderation'
			},
			{
				label: 'Issues',
				to: '/app/admin/issues',
				icon: 'bi-flag',
				roles: ['admin'],
				adminCapability: 'admin_issues',
				tip: 'User reports (e.g. garden not detected)'
			},
			{
				label: 'Errors',
				to: '/app/admin/errors',
				icon: 'bi-bug',
				roles: ['admin'],
				adminCapability: 'admin_errors',
				tip: 'Plan creation failures for debugging'
			}
		]
	}
];

export default function AdminLayout() {
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const isMobileLayout = useMediaQuery(MOBILE_SIDEBAR_MQ);
	const logoSrc = `${import.meta.env.BASE_URL}logo-transparent.png`;
	const [isSidebarOpen, setIsSidebarOpen] = useState(() =>
		typeof window !== 'undefined' ? !window.matchMedia(MOBILE_SIDEBAR_MQ).matches : true
	);
	const { user } = useAuth();
	const location = useLocation();
	const contentRef = useRef<HTMLElement | null>(null);

	const handleToggleSidebar = () => setIsSidebarOpen((prev) => !prev);

	const closeDrawerIfMobile = useCallback(() => {
		if (isMobileLayout) {
			setIsSidebarOpen(false);
		}
	}, [isMobileLayout]);

	useEffect(() => {
		if (isMobileLayout) {
			setIsSidebarOpen(false);
		}
	}, [location.pathname, isMobileLayout]);

	useEffect(() => {
		contentRef.current?.scrollTo({ top: 0, behavior: 'auto' });
		window.scrollTo({ top: 0, behavior: 'auto' });
		document.documentElement.scrollTop = 0;
		document.body.scrollTop = 0;
	}, [location.pathname, location.search]);

	useEffect(() => {
		if (!isMobileLayout || !isSidebarOpen) {
			return;
		}
		const prev = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = prev;
		};
	}, [isMobileLayout, isSidebarOpen]);

	useEffect(() => {
		if (!isMobileLayout || !isSidebarOpen) {
			return;
		}
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				setIsSidebarOpen(false);
			}
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [isMobileLayout, isSidebarOpen]);

	const filteredSections = useMemo(() => {
		if (!user) {
			return [];
		}
		return NAV_SECTIONS.map((section) => ({
			...section,
			items: section.items.filter(
				(item) =>
					item.roles.includes(user.role) &&
					!item.hidden &&
					(!item.adminCapability || hasAdminCapability(user, item.adminCapability)) &&
					(!item.appCapability || hasAppCapability(user, item.appCapability))
			)
		})).filter((section) => section.items.length > 0);
	}, [user]);

	if (!user) {
		return null;
	}

	return (
		<div className="d-flex sb-layout">
			{isMobileLayout && isSidebarOpen && (
				<button
					type="button"
					className="sb-drawer-backdrop"
					aria-label={copy('Close navigation menu', 'Navigationsmenü schließen', 'Zatvori navigacijski izbornik')}
					onClick={() => setIsSidebarOpen(false)}
				/>
			)}
			<aside className={`sb-sidebar border-end${isSidebarOpen ? '' : ' collapsed'}`}>
				<div className="sb-sidebar__content">
					<Link
						to="/app/home"
						className="p-3 d-flex align-items-center gap-2 sb-brand text-decoration-none"
						onClick={closeDrawerIfMobile}
					>
						<img
							src={logoSrc}
							alt="HortEra logo"
							className="gh-logo gh-logo--sidebar"
						/>
						<span className="fw-bold sb-brand-text">HortEra</span>
					</Link>
					<nav className="nav flex-column px-2">
						{filteredSections.map((section) => (
							<div key={section.key} className="mb-2">
								{section.title && (
									<div className="text-uppercase text-muted small mt-3 mb-1 sb-nav-section-title">
										{section.title === 'Gardener'
											? copy('Gardener', 'Gärtner', 'Vrtlar')
											: section.title === 'Garden Seekers'
												? copy('Garden Seekers', 'Gartensuchende', 'Vlasnici vrta')
												: section.title === 'Admin'
													? copy('Admin', 'Admin', 'Admin')
													: section.title}
									</div>
								)}
								{section.items.map((item) => {
									// Special handling for "Create Plan" - clear editingPlanId
									if (item.to.startsWith('/app/seeker/create-plan')) {
										return (
											<NavLink
												key={item.to}
												to={item.to}
												className="nav-link"
												title={item.label}
												onClick={() => {
													localStorage.removeItem('editingPlanId');
													localStorage.removeItem('gardenQuestionnaireDraft');
													closeDrawerIfMobile();
												}}
											>
												<i className={`${item.icon} me-2`}></i>
												<span className="sb-nav-label">
													{item.label === 'Dashboard' ? copy('Dashboard', 'Dashboard', 'Nadzorna ploča') :
														item.label === 'Marketplace' ? copy('Marketplace', 'Marktplatz', 'Tržnica') :
														item.label === 'Direct plans' ? copy('Direct plans', 'Direkte Pläne', 'Izravni planovi') :
														item.label === 'Projects' ? copy('Projects', 'Projekte', 'Projekti') :
														item.label === 'Schedule' ? copy('Schedule', 'Zeitplan', 'Raspored') :
														item.label === 'Tasks' ? copy('Tasks', 'Zadaci', 'Zadaci') :
														item.label === 'Team' ? copy('Team', 'Team', 'Tim') :
														item.label === 'My Plans' ? copy('My Plans', 'Meine Pläne', 'Moji planovi') :
														item.label === 'My Projects' ? copy('My Projects', 'Meine Projekte', 'Moji projekti') :
														item.label === 'Create Plan' ? copy('Create Plan', 'Plan erstellen', 'Kreiraj plan') :
														item.label === 'Admin Insights' ? copy('Admin Insights', 'Admin-Einblicke', 'Admin uvidi') :
														item.label === 'Engagement' ? copy('Engagement', 'Engagement', 'Angažman') :
														item.label === 'User activity' ? copy('User activity', 'Nutzeraktivität', 'Aktivnost korisnika') :
														item.label === 'Moderation' ? copy('Moderation', 'Moderation', 'Moderacija') :
														item.label === 'Issues' ? copy('Issues', 'Probleme', 'Prijave') :
														item.label === 'Errors' ? copy('Errors', 'Fehler', 'Greške') :
														item.label}
												</span>
											</NavLink>
										);
									}
									return (
										<NavLink
											key={item.to}
											to={item.to}
											className="nav-link"
											title={item.label}
											onClick={closeDrawerIfMobile}
										>
											<i className={`${item.icon} me-2`}></i>
											<span className="sb-nav-label">
												{item.label === 'Dashboard' ? copy('Dashboard', 'Dashboard', 'Nadzorna ploča') :
													item.label === 'Marketplace' ? copy('Marketplace', 'Marktplatz', 'Tržnica') :
													item.label === 'Direct plans' ? copy('Direct plans', 'Direkte Pläne', 'Izravni planovi') :
													item.label === 'Projects' ? copy('Projects', 'Projekte', 'Projekti') :
													item.label === 'Schedule' ? copy('Schedule', 'Zeitplan', 'Raspored') :
													item.label === 'Tasks' ? copy('Tasks', 'Aufgaben', 'Zadaci') :
													item.label === 'Team' ? copy('Team', 'Team', 'Tim') :
													item.label === 'My Plans' ? copy('My Plans', 'Meine Pläne', 'Moji planovi') :
													item.label === 'My Projects' ? copy('My Projects', 'Meine Projekte', 'Moji projekti') :
													item.label === 'Create Plan' ? copy('Create Plan', 'Plan erstellen', 'Kreiraj plan') :
													item.label === 'Admin Insights' ? copy('Admin Insights', 'Admin-Einblicke', 'Admin uvidi') :
													item.label === 'Engagement' ? copy('Engagement', 'Engagement', 'Angažman') :
													item.label === 'User activity' ? copy('User activity', 'Nutzeraktivität', 'Aktivnost korisnika') :
													item.label === 'Moderation' ? copy('Moderation', 'Moderation', 'Moderacija') :
													item.label === 'Issues' ? copy('Issues', 'Probleme', 'Prijave') :
													item.label === 'Errors' ? copy('Errors', 'Fehler', 'Greške') :
													item.label}
											</span>
										</NavLink>
									);
								})}
							</div>
						))}
					</nav>
					{/* Calendar - shows when expanded or on hover when collapsed */}
					<div className="sb-calendar-wrapper">
						<MiniCalendar />
					</div>
					{/* FAQ Section */}
					<div className="sb-faq-wrapper">
						<SidebarFAQ />
					</div>
				</div>
			</aside>
			<main ref={contentRef} className="flex-grow-1 sb-content">
				<Topbar
					onToggleSidebar={handleToggleSidebar}
					isSidebarOpen={isSidebarOpen}
					isMobileLayout={isMobileLayout}
				/>
				<EmailVerificationBanner />
				<AdminViewAsStrip />
				<div className="container-fluid py-4">
					<Outlet />
				</div>
				<DeadlineChecker />
			</main>
		</div>
	);
}


